/**
 * <ai-variant-selector> — 纯业务变体选择组件
 *
 * 职责：
 * - 渲染选项组 UI（颜色色板/图片色板/文本按钮）
 * - 管理选中状态、匹配变体
 * - 派发 variant:change 事件
 * - 不负责 Cart API、不渲染 drawer/modal 外壳
 *
 * 用法（内联 — product-spotlight 等）：
 * <ai-variant-selector
 *   variants='{{ product.variants | json }}'
 *   options='{{ product.options_with_values | json }}'
 *   selected-variant-id="{{ product.selected_or_first_available_variant.id }}"
 * ></ai-variant-selector>
 *
 * DOM 结构（渲染就地，样式由 LLM 在 section CSS 中定义）：
 *
 * <ai-variant-selector>
 *   .vs-product-price               ← 价格（选中变体后自动更新）
 *   .vs-option-group[data-option-name]
 *     .vs-option-label              ← 选项名
 *     .vs-option-values             ← 选项值容器
 *       button.vs-option-btn[data-value-id][data-swatch-type]
 *         data-swatch-type="image"  → 内含 img.vs-swatch-img
 *         data-swatch-type="color"  → 内含 span.vs-swatch-color（带 background-color）
 *         data-swatch-type="text"   → 纯文本
 *       .vs-option-btn--active      ← 选中态
 *   .vs-variant-status              ← 可选：售罄提示
 *
 * 事件：
 * - variant:change  { variantId, variant, available, price }
 *
 * 属性变化：
 * - 选中变体后自动更新 selected-variant-id attribute
 * - 暴露 data-available / data-sold-out 属性
 */

if (!customElements.get("ai-variant-selector")) {
    customElements.define(
        "ai-variant-selector",
        class VariantSelector extends HTMLElement {
            _variants = [];
            _options = [];
            _selectedOptions = {}; // { optionIndex: { id, name } }
            _selectedVariantId = null;
            _rendered = false;
            _suppressAttributeSync = false;

            // ══════════════════════════════════════════
            //  生命周期
            // ══════════════════════════════════════════

            connectedCallback() {
                // 优先从属性读，fallback 到父容器 <script> 标签
                this._variants = this._parseJSON(this.getAttribute("variants")) || this._getProductData("variants") || [];
                this._options = this._parseJSON(this.getAttribute("options")) || this._getProductData("options") || [];
                this._selectedVariantId =
                    this.getAttribute("selected-variant-id") || null;

                if (!this._rendered) {
                    if (this.querySelector(".vs-option-group")) {
                        this._hydrate();
                    } else {
                        this._render();
                    }
                    this._rendered = true;
                }

                this._preselectDefaultVariant();
            }

            static get observedAttributes() {
                return ["variants", "options", "selected-variant-id"];
            }

            attributeChangedCallback(name, oldVal, newVal) {
                if (oldVal === newVal || !this._rendered || this._suppressAttributeSync) return;

                if (name === "variants") {
                    this._variants = this._parseJSON(newVal) || [];
                    this._rerender();
                } else if (name === "options") {
                    this._options = this._parseJSON(newVal) || [];
                    this._rerender();
                } else if (name === "selected-variant-id") {
                    this._selectedVariantId = newVal;
                    this._preselectDefaultVariant();
                }
            }

            // ══════════════════════════════════════════
            //  渲染
            // ══════════════════════════════════════════

            _hydrate() {
                // 服务端已渲染 DOM，只绑定事件不追加节点
                if (!this._shouldRenderPrice()) {
                    this.querySelector(".vs-product-price")?.remove();
                } else if (!this.querySelector(".vs-product-price")) {
                    const priceEl = document.createElement("div");
                    priceEl.className = "vs-product-price";
                    const defaultVariant = this._getDefaultVariant();
                    this._setPriceText(priceEl, defaultVariant);
                    this.insertBefore(priceEl, this.firstChild);
                }

                this._reconcileOptionValuesFromDom();

                const groups = this.querySelectorAll(".vs-option-group");
                groups.forEach((group, optIdx) => {
                    const valuesContainer = group.querySelector(".vs-option-values");
                    if (!valuesContainer) return;
                    valuesContainer.querySelectorAll(".vs-option-btn").forEach(btn => {
                        btn.addEventListener("click", () => {
                            this._selectOption(optIdx, btn, valuesContainer);
                        });
                    });
                });
            }

            /**
             * 用服务端 DOM 上的真实 value id 回填 JSON 数据。
             *
             * Liquid drop 渲染按钮时带有 option value id（data-value-id），但
             * `options_with_values | json` 在部分店铺序列化出的 values 是纯
             * 名称字符串、option.id 为空。不回填的话，点击产生的数字 id 与
             * 变体侧的名称 token 永远对不上 → 无默认选中、整页 Unavailable。
             */
            _reconcileOptionValuesFromDom() {
                this.querySelectorAll(".vs-option-group").forEach((group, optIdx) => {
                    const option = this._options[optIdx];
                    if (!option || !Array.isArray(option.values)) return;

                    group.querySelectorAll(".vs-option-btn").forEach(btn => {
                        const domId = btn.getAttribute("data-value-id");
                        if (!domId) return;
                        const matchBy = names => option.values.findIndex(candidate => names.some(name =>
                            candidate === name ||
                            (typeof candidate === "object" && candidate !== null &&
                                this._getOptionValueName(candidate) === name),
                        ));
                        const exactNames = [btn.getAttribute("title"), btn.textContent.trim()].filter(Boolean);
                        if (!exactNames.length) return;
                        let index = matchBy(exactNames);
                        if (index < 0) {
                            // title 在历史版本未转义，含引号的名称（如 24"）会被
                            // 浏览器截断为 24 —— 精确匹配失败后补一次带引号的尝试
                            index = matchBy(exactNames.map(name => `${name}"`));
                        }
                        if (index < 0) return;

                        const candidate = option.values[index];
                        if (typeof candidate !== "object" || candidate === null) {
                            // 名称以 JSON 侧为准 —— 变体的 options 引用的是它
                            option.values[index] = { id: domId, name: candidate };
                        } else if (this._getOptionValueId(candidate) == null) {
                            candidate.id = domId;
                        }
                    });
                });
            }

            _render() {
                // 价格
                if (this._shouldRenderPrice()) {
                    const priceEl = document.createElement("div");
                    priceEl.className = "vs-product-price";
                    const defaultVariant = this._getDefaultVariant();
                    this._setPriceText(priceEl, defaultVariant);
                    this.appendChild(priceEl);
                }

                // 选项组
                this._options.forEach((option, optIdx) => {
                    this.appendChild(this._createOptionGroup(option, optIdx));
                });
            }

            _rerender() {
                this.innerHTML = "";
                this._selectedOptions = {};
                this._rendered = false;
                this._render();
                this._rendered = true;
                this._preselectDefaultVariant();
            }

            // ══════════════════════════════════════════
            //  UI 构建
            // ══════════════════════════════════════════

            _createOptionGroup(option, optIdx) {
                const group = document.createElement("div");
                group.className = "vs-option-group";
                group.setAttribute("data-option-name", option.name);
                if (option.id != null)
                    group.setAttribute("data-option-id", String(option.id));

                const label = document.createElement("span");
                label.className = "vs-option-label";
                label.textContent = option.name;
                group.appendChild(label);

                const valuesContainer = document.createElement("div");
                valuesContainer.className = "vs-option-values flex flex-wrap gap-2";

                option.values.forEach(val => {
                    valuesContainer.appendChild(
                        this._createOptionBtn(option, val, optIdx, valuesContainer),
                    );
                });

                group.appendChild(valuesContainer);
                return group;
            }

            _shouldRenderPrice() {
                return !this.hasAttribute("data-hide-price") &&
                    this.getAttribute("show-price") !== "false";
            }

            _createOptionBtn(option, val, optIdx, valuesContainer) {
                const resolved = this._resolveOptionValue(val);
                // 字符串 value 没有 id 时退化为名称 token，保证点击产生的
                // 选中值与 getVariantOptionValueIds 的 token 始终同源可比。
                const rawValId = this._getOptionValueId(val);
                const valId = rawValId == null ? null : String(rawValId);

                const btn = document.createElement("button");
                btn.className = "vs-option-btn";
                btn.type = "button";
                btn.setAttribute("data-swatch-type", resolved.type);
                if (option.id != null)
                    btn.setAttribute("data-option-id", String(option.id));
                if (valId) btn.setAttribute("data-value-id", valId);

                // swatch 渲染：image > color > text
                if (resolved.type === "image") {
                    const img = document.createElement("img");
                    img.className = "vs-swatch-img";
                    img.src = resolved.swatchImage;
                    img.alt = resolved.name;
                    img.loading = "lazy";
                    btn.appendChild(img);
                    btn.setAttribute("title", resolved.name);
                } else if (resolved.type === "color") {
                    const swatch = document.createElement("span");
                    swatch.className = "vs-swatch-color";
                    swatch.style.backgroundColor = resolved.swatchColor;
                    btn.appendChild(swatch);
                    btn.setAttribute("title", resolved.name);
                } else {
                    btn.textContent = resolved.name;
                }

                btn.addEventListener("click", () => {
                    this._selectOption(optIdx, btn, valuesContainer, resolved.name);
                });

                return btn;
            }

            // ══════════════════════════════════════════
            //  选项逻辑：预选 / 选中 / 匹配
            // ══════════════════════════════════════════

            _preselectDefaultVariant() {
                const defaultVariant = this._getDefaultVariant();
                if (!defaultVariant) return;

                const variantValueIds = this.getVariantOptionValueIds(defaultVariant);
                if (!variantValueIds.some(id => id != null)) return;

                this.applyOptionValueIds(variantValueIds, { notify: true });
            }

            _onOptionSelected() {
                const selectedOptionValueIds = this._getSelectedOptionValueIds();
                const optionValueStates = this.updateOptionValuesState(
                    this,
                    selectedOptionValueIds,
                );
                this.syncStickyCartOptionValuesState(selectedOptionValueIds);

                // 未选完 → 通知但不匹配
                if (selectedOptionValueIds.some(id => id == null)) {
                    return;
                }

                const matched = this.findVariantByOptionValueIds(selectedOptionValueIds);

                if (!matched) {
                    this._setVariantAvailability(null);
                    this._dispatchVariantChange(null, selectedOptionValueIds, optionValueStates);
                    return;
                }

                this._selectedVariantId = matched.id;
                this._syncSelectedVariantAttribute(matched.id);

                this._updatePrice(matched);

                this._setVariantAvailability(matched);
                this._dispatchVariantChange(matched, selectedOptionValueIds, optionValueStates);
            }

            _selectOption(optIdx, btn, valuesContainer, fallbackName = "") {
                valuesContainer
                    ?.querySelectorAll(".vs-option-btn")
                    .forEach(b => b.classList.remove("vs-option-btn--active"));
                btn.classList.add("vs-option-btn--active");
                const valId = btn.getAttribute("data-value-id");
                const name = fallbackName || btn.getAttribute("title") || btn.textContent.trim();
                this._selectedOptions[optIdx] = { id: valId, name };
                this._onOptionSelected();
            }

            getVariantOptionValueIds(variant) {
                const rawValues = Array.isArray(variant?.options) && variant.options.length
                    ? variant.options
                    : [variant?.option1, variant?.option2, variant?.option3];

                return this._options.map((option, index) => {
                    const raw = rawValues[index];
                    if (raw == null) return null;

                    // 对象形态（含 id）直接取 id；Shopify 变体 JSON 的
                    // options 是纯名称字符串（["Black", "24\""]），必须按
                    // 位置在 options_with_values 里把名称解析成 value id，
                    // 才能与按钮上的 data-value-id 对得上。
                    if (typeof raw === "object") {
                        const id = this._getOptionValueId(raw);
                        return id == null ? null : String(id);
                    }

                    const rawText = String(raw);
                    const matched = (option?.values || []).find(candidate =>
                        String(this._getOptionValueId(candidate)) === rawText ||
                        this._getOptionValueName(candidate) === rawText,
                    );
                    if (matched) {
                        const id = this._getOptionValueId(matched);
                        if (id != null) return String(id);
                    }
                    return rawText;
                });
            }

            findVariantByOptionValueIds(selectedOptionValueIds) {
                if (!this._variants?.length || !selectedOptionValueIds?.length) {
                    return null;
                }

                const selectedIds = selectedOptionValueIds.map(id => id == null ? null : String(id));
                if (
                    selectedIds.length !== this._options.length ||
                    selectedIds.some(id => id == null)
                ) {
                    return null;
                }

                return this._variants.find(variant => {
                    const variantValueIds = this.getVariantOptionValueIds(variant);
                    if (variantValueIds.length !== selectedIds.length) return false;
                    return selectedIds.every((id, index) => variantValueIds[index] === id);
                }) || null;
            }

            isOptionValueAvailableForSelection(
                optionIndex,
                optionValueId,
                selectedOptionValueIds,
            ) {
                if (!this._variants?.length) return false;

                return this._variants.some(variant => {
                    if (!variant?.available) return false;

                    const variantValueIds = this.getVariantOptionValueIds(variant);
                    if (variantValueIds[optionIndex] !== String(optionValueId)) {
                        return false;
                    }

                    return selectedOptionValueIds.every((selectedId, selectedIndex) => {
                        if (selectedId == null || selectedIndex === optionIndex) return true;
                        return variantValueIds[selectedIndex] === String(selectedId);
                    });
                });
            }

            updateOptionValuesState(
                variantSelects = this,
                selectedOptionValueIds = this._getSelectedOptionValueIds(),
            ) {
                if (!variantSelects || !this._options?.length) return [];

                const states = [];
                this._options.forEach((option, optionIndex) => {
                    (option.values || []).forEach(value => {
                        const valueId = this._getOptionValueId(value);
                        if (valueId == null) return;

                        const optionElement = this._findOptionElement(variantSelects, option, valueId);
                        if (!optionElement) return;

                        const isSelected = selectedOptionValueIds[optionIndex] === String(valueId);
                        const isAvailable = this.isOptionValueAvailableForSelection(
                            optionIndex,
                            valueId,
                            selectedOptionValueIds,
                        );

                        optionElement.classList.toggle("vs-option-btn--active", isSelected);
                        optionElement.classList.toggle("disabled", !isAvailable);
                        optionElement.classList.toggle("unavailable", !isAvailable);
                        optionElement.setAttribute("aria-disabled", isAvailable ? "false" : "true");
                        optionElement.setAttribute("aria-pressed", isSelected ? "true" : "false");
                        optionElement.toggleAttribute("data-unavailable", !isAvailable);

                        states.push({
                            optionIndex,
                            optionId: option.id == null ? null : String(option.id),
                            valueId: String(valueId),
                            selected: isSelected,
                            available: isAvailable,
                        });
                    });
                });

                return states;
            }

            syncStickyCartOptionValuesState(selectedOptionValueIds) {
                if (typeof document === "undefined") return;

                document
                    .querySelectorAll(
                        "[data-sticky-cart] ai-variant-selector, .product-sticky-cart ai-variant-selector, sticky-cart ai-variant-selector, [data-sticky-cart-variants] ai-variant-selector",
                    )
                    .forEach(selector => {
                        if (selector === this) return;
                        if (typeof selector.applyOptionValueIds === "function") {
                            selector.applyOptionValueIds(selectedOptionValueIds);
                        } else {
                            this.updateOptionValuesState(selector, selectedOptionValueIds);
                        }
                    });
            }

            applyOptionValueIds(selectedOptionValueIds, { notify = false } = {}) {
                this._selectedOptions = {};
                const normalizedIds = this._options.map((option, index) => {
                    const id = selectedOptionValueIds[index];
                    if (id == null) return null;
                    const value = (option.values || []).find(
                        candidate => String(this._getOptionValueId(candidate)) === String(id),
                    );
                    if (!value) return null;
                    this._selectedOptions[index] = {
                        id: String(id),
                        name: this._getOptionValueName(value),
                    };
                    return String(id);
                });

                const optionValueStates = this.updateOptionValuesState(this, normalizedIds);
                const matched = this.findVariantByOptionValueIds(normalizedIds);
                if (matched) {
                    this._selectedVariantId = matched.id;
                    this._syncSelectedVariantAttribute(matched.id);
                    this._updatePrice(matched);
                    this._setVariantAvailability(matched);
                }

                if (notify) {
                    this.syncStickyCartOptionValuesState(normalizedIds);
                    if (matched) {
                        this._dispatchVariantChange(matched, normalizedIds, optionValueStates);
                    }
                }
            }

            _dispatchVariantChange(variant, selectedOptionValueIds, optionValueStates) {
                this.dispatchEvent(
                    new CustomEvent("variant:change", {
                        bubbles: true,
                        detail: {
                            variantId: variant?.id || null,
                            variant,
                            available: !!variant?.available,
                            price: variant?.price,
                            selectedOptionValueIds,
                            optionValueStates,
                        },
                    }),
                );
            }

            _setVariantAvailability(variant) {
                if (variant?.available) {
                    this.setAttribute("data-available", "");
                    this.removeAttribute("data-sold-out");
                } else {
                    this.removeAttribute("data-available");
                    this.setAttribute("data-sold-out", "");
                }
            }

            _syncSelectedVariantAttribute(variantId) {
                if (variantId == null || String(this.getAttribute("selected-variant-id")) === String(variantId)) {
                    return;
                }
                this._suppressAttributeSync = true;
                this.setAttribute("selected-variant-id", String(variantId));
                this._suppressAttributeSync = false;
            }

            _getSelectedOptionValueIds() {
                return this._options.map((_, index) => {
                    const id = this._selectedOptions[index]?.id;
                    return id == null ? null : String(id);
                });
            }

            _updatePrice(variant) {
                const priceEl = this.querySelector(".vs-product-price");
                if (priceEl) this._setPriceText(priceEl, variant);
            }

            _setPriceText(priceEl, variant) {
                const price = this._formatVariantPrice(variant);
                if (price) priceEl.textContent = price;
            }

            _formatVariantPrice(variant) {
                const rawPrice = variant?.price;
                if (rawPrice == null || rawPrice === "") return "";
                const rawText = String(rawPrice).trim();
                if (!rawText) return "";

                // Some platforms already serialize formatted money into variant.price.
                if (/[^\d\s,.-]/.test(rawText)) return rawText;

                const template = (this.getAttribute("price-template") || "").trim();
                if (!template) return rawText;

                const templateNumber = template.match(/\d[\d,]*(?:\.\d+)?/);
                if (!templateNumber) return rawText;

                const decimals = templateNumber[0].includes(".")
                    ? templateNumber[0].split(".").pop().length
                    : 0;
                const numeric = Number(rawText.replace(/,/g, ""));
                const replacement = Number.isFinite(numeric) ? numeric.toFixed(decimals) : rawText;
                return template.replace(templateNumber[0], replacement);
            }

            // ══════════════════════════════════════════
            //  数据解析工具
            // ══════════════════════════════════════════

            _getDefaultVariant() {
                return (
                    this._variants.find(
                        v => String(v.id) === String(this._selectedVariantId),
                    ) || this._variants[0]
                );
            }

            _getOptionValueId(optionValue) {
                if (optionValue == null) return null;
                if (typeof optionValue !== "object") return optionValue;
                return optionValue.values?.id ??
                    optionValue.value?.id ??
                    optionValue.id ??
                    optionValue.value_id ??
                    null;
            }

            _getOptionValueName(optionValue) {
                if (optionValue == null) return "";
                if (typeof optionValue !== "object") return String(optionValue);
                const rawValue = optionValue.value;
                return optionValue.name ||
                    optionValue.label ||
                    optionValue.title ||
                    (typeof rawValue === "string" || typeof rawValue === "number" ? String(rawValue) : "") ||
                    String(this._getOptionValueId(optionValue) || "");
            }

            _findOptionElement(variantSelects, option, valueId) {
                // 名称型 token 可能含引号（如 24"），必须转义后再拼属性选择器
                const value = this._escapeAttrValue(valueId);
                const selectors = [];
                if (option.id != null) {
                    const optionId = this._escapeAttrValue(option.id);
                    selectors.push(`[data-option-id="${optionId}"][data-value-id="${value}"]`);
                    selectors.push(`[data-option-id="${optionId}"][data-option-value-id="${value}"]`);
                }
                selectors.push(`[data-value-id="${value}"]`);
                selectors.push(`[data-option-value-id="${value}"]`);

                return selectors
                    .map(selector => variantSelects.querySelector(selector))
                    .find(Boolean) || null;
            }

            _escapeAttrValue(value) {
                return String(value).replace(/\\/g, "\\\\").replace(/"/g, '\\"');
            }

            /**
             * 解析 option value 的 swatch 数据
             * 优先级：swatch.image > swatch.color > name 文本
             */
            _resolveOptionValue(val) {
                if (typeof val !== "object" || val === null) {
                    return { name: String(val), type: "text" };
                }

                const name = val.name || val.value || val.label || val.title || "";
                const swatch = val.swatch;

                if (swatch && typeof swatch === "object") {
                    if (swatch.image) {
                        const imgSrc =
                            typeof swatch.image === "object"
                                ? swatch.image.src || swatch.image.url || ""
                                : String(swatch.image);
                        if (imgSrc) {
                            return { name, type: "image", swatchImage: imgSrc };
                        }
                    }
                    if (swatch.color) {
                        return { name, type: "color", swatchColor: swatch.color };
                    }
                }

                return { name: name || JSON.stringify(val), type: "text" };
            }

            _parseJSON(str) {
                if (!str) return null;
                try {
                    return JSON.parse(str);
                } catch {
                    return null;
                }
            }

            /**
             * 从父容器（ai-product-detail / ai-product-card）的 script 标签读取数据
             * @param {'variants'|'options'} key
             * @returns {Array|null}
             */
            _getProductData(key) {
                const container = this.closest("ai-product-detail, ai-product-card");
                if (!container) return null;
                const script = container.querySelector(`script[data-product-${key}]`);
                if (!script) return null;
                try {
                    return JSON.parse(script.textContent);
                } catch {
                    return null;
                }
            }
        },
    );
}
